import React, { useState, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { LabeledField } from '@/__core/components/LabeledField';

interface AddButtonAutocompleteProps {
  options: Array<{ id: string; name: string; [key: string]: any }>;
  value: any;
  onChange: (event: any, newValue: any) => void;
  onBlur?: (event: any) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: string) => void;
  placeholder?: string;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  onAddNew?: (newItem: { name: string }) => Promise<{ id: string; name: string }>;
  addDialogTitle?: string;
  addDialogLabel?: string;
  addDialogPlaceholder?: string;
  sx?: any;
  // Props for custom dialog support
  CustomDialog?: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => Promise<any>;
    loading?: boolean;
  }>;
  onAddNewCustom?: (data: any) => Promise<{ id: string; name: string }>;
  // Permission control for '+' button visibility
  canAddNew?: boolean;
  // Props for custom autocomplete configuration
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  filterOptions?: (options: any[], { inputValue }: { inputValue: string }) => any[];
  renderOption?: (props: any, option: any) => React.ReactNode;
  renderInput?: (params: any) => React.ReactNode;
  groupBy?: (option: any) => string;
  noOptionsText?: string;
  clearOnEscape?: boolean;
  disableClearable?: boolean;
  selectOnFocus?: boolean;
  handleHomeEndKeys?: boolean;
  disableGroupSticky?: boolean;
}

const AddButtonAutocomplete: React.FC<AddButtonAutocompleteProps> = ({
  options,
  value,
  onChange,
  onBlur,
  onInputChange,
  placeholder = "Chọn...",
  label,
  size = "small",
  fullWidth = true,
  disabled = false,
  loading: autocompleteLoading = false,
  required = false,
  onAddNew,
  addDialogTitle = "Thêm mới",
  addDialogLabel = "Tên",
  addDialogPlaceholder = "Nhập tên...",
  sx,
  CustomDialog,
  onAddNewCustom,
  canAddNew = true,
  getOptionLabel = (option: any) => option?.name || '',
  isOptionEqualToValue = (option: any, value: any) => option?.id === value?.id,
  filterOptions,
  renderOption,
  renderInput: customRenderInput,
  groupBy,
  noOptionsText = "Không tìm thấy",
  clearOnEscape = true,
  disableClearable = false,
  selectOnFocus = true,
  handleHomeEndKeys = true,
  disableGroupSticky = false,
}) => {
  const [open, setOpen] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddNew = useCallback(async () => {
    if (!newItemName.trim() || !onAddNew) return;

    try {
      setLoading(true);
      setError(null);
      const newItem = await onAddNew({ name: newItemName.trim() });
      
      // Add new item to options and select it
      const updatedOptions = [...options, newItem];
      onChange(null, newItem);
      
      setNewItemName('');
      setOpen(false);
    } catch (error: any) {
      setError(error.message || 'Có lỗi xảy ra khi tạo mới');
    } finally {
      setLoading(false);
    }
  }, [newItemName, onAddNew, options, onChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setNewItemName('');
    setError(null);
  }, []);

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddNew();
    }
  }, [handleAddNew]);

  const handleOpenDialog = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          width: fullWidth ? '100%' : 'auto',
          minWidth: 0,
          ...sx,
        }}
        className="AddButtonAutocomplete"
      >
        <Autocomplete
          size={size}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onInputChange={onInputChange}
          options={options}
          loading={autocompleteLoading}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          filterOptions={filterOptions}
          renderOption={renderOption}
          groupBy={groupBy}
          renderGroup={(params) => (
            <li key={params.key} style={{ padding: 0, margin: 0 }}>
              <ListSubheader 
                disableSticky={disableGroupSticky}
                sx={{ 
                  fontWeight: 600, 
                  backgroundColor: '#f9fafb',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#6b7280',
                  py: '5px !important',
                  pb: '2px !important',
                  mb: '0 !important',
                  mt: '0 !important',
                  minHeight: 'auto !important',
                  lineHeight: '1.2 !important',
                  paddingLeft: '8px',
                }}
              >
                {params.group}
              </ListSubheader>
              {params.children}
            </li>
          )}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiAutocomplete-option': {
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '&[aria-selected="true"]': {
                backgroundColor: 'transparent',
              },
            },
            '& .MuiAutocomplete-groupLabel': {
              paddingTop: '2px !important',
              paddingBottom: '2px !important',
              marginBottom: '0 !important',
            },
            '& .MuiAutocomplete-groupUl': {
              paddingTop: '0 !important',
              marginTop: '0 !important',
              paddingBottom: '0 !important',
            },
            '& .MuiAutocomplete-listbox': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
            },
          }}
          renderInput={customRenderInput || ((params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiAutocomplete-clearIndicator': {
                  display: 'none',
                },
                '& .MuiAutocomplete-popupIndicator': {
                  display: 'none',
                },
              }}
            />
          ))}
          noOptionsText={noOptionsText}
          clearOnEscape={clearOnEscape}
          disableClearable={disableClearable}
          selectOnFocus={selectOnFocus}
          handleHomeEndKeys={handleHomeEndKeys}
          disabled={disabled}
          fullWidth={fullWidth}
        />
        
        {canAddNew && (onAddNew || (CustomDialog && onAddNewCustom)) && (
          <IconButton
            className="AddButtonAutocomplete__icon"
            onClick={handleOpenDialog}
            disabled={disabled}
            sx={{
              position: 'relative',
              color: '#000000',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&:disabled': {
                color: '#d1d5db',
              },
              width: '20px',
              height: '20px',
              zIndex: 1,
            }}
            size="small"
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* Add New Dialog */}
      {CustomDialog && onAddNewCustom ? (
        <CustomDialog
          open={open}
          onClose={handleClose}
          onSave={onAddNewCustom}
          loading={loading}
        />
      ) : (
        onAddNew && (
          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <DialogTitle sx={{ 
              borderBottom: '1px solid #e5e7eb',
              pb: 2,
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#111827'
            }}>
              {addDialogTitle}
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
              <LabeledField
                label={addDialogLabel}
                error={!!error}
                helperText={error || undefined}
                labelWidth={120}
              >
                <TextField
                  fullWidth
                  placeholder={addDialogPlaceholder}
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  error={!!error}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                  }}
                />
              </LabeledField>
            </DialogContent>

            <DialogActions sx={{ 
              borderTop: '1px solid #e5e7eb',
              p: 2,
              gap: 2,
            }}>
              <Button 
                onClick={handleClose}
                variant="outlined"
                disabled={loading}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                Hủy
              </Button>
              <Button
                onClick={handleAddNew}
                variant="contained"
                disabled={!newItemName.trim() || loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                sx={{
                  backgroundColor: '#10b981',
                  '&:hover': {
                    backgroundColor: '#059669',
                  },
                  '&:disabled': {
                    backgroundColor: '#d1d5db',
                    color: '#9ca3af',
                  },
                }}
              >
                {loading ? 'Đang tạo...' : 'Tạo mới'}
              </Button>
            </DialogActions>
          </Dialog>
        )
      )}
    </>
  );
};

export default AddButtonAutocomplete;
